import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "../common/Modal";
import Input from "../common/Input";
import Button from "../common/Button";
import {
  RestaurantCreateFormData,
  RestaurantUpdateFormData,
  restaurantCreateSchema,
  restaurantUpdateSchema,
  Restaurant,
} from "../../schemas/restaurantSchema";

interface RestaurantFormProps {
  isOpen: boolean;
  initialData?: Restaurant | RestaurantUpdateFormData;
  onSubmit: (data: RestaurantCreateFormData | RestaurantUpdateFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isUpdate?: boolean;
}

const RestaurantForm: React.FC<RestaurantFormProps> = ({
  isOpen,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isUpdate = false,
}) => {
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
    trigger,
  } = useForm<RestaurantCreateFormData | RestaurantUpdateFormData>({
    resolver: zodResolver(isUpdate ? restaurantUpdateSchema : restaurantCreateSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      code: initialData?.code || "",
      name: initialData?.name || "",
      description: initialData?.description || "",
    },
  });

  // Track if modal was just opened to prevent resetting during submission
  const prevIsOpenRef = useRef(false);

  // Reset form only when modal opens (not on every render or during submission)
  useEffect(() => {
    // Only reset when modal transitions from closed to open
    if (isOpen && !prevIsOpenRef.current) {
      if (initialData) {
        reset({
          code: initialData.code || "",
          name: initialData.name || "",
          description: initialData.description || "",
        });
      } else {
        reset({
          code: "",
          name: "",
          description: "",
        });
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialData, reset]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={initialData ? "Edit Restaurant" : "Create Restaurant"}
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
        <Input
          label="Code"
          placeholder="Enter restaurant code"
          name="code"
          value={watch("code") || ""}
          onChange={(e) => setValue("code", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("code")}
          error={errors.code}
        />
        <Input
          label="Name"
          placeholder="Enter restaurant name"
          name="name"
          value={watch("name") || ""}
          onChange={(e) => setValue("name", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("name")}
          error={errors.name}
        />
        <Input
          label="Description"
          placeholder="Enter description (optional)"
          name="description"
          value={watch("description") || ""}
          onChange={(e) => setValue("description", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("description")}
          error={errors.description}
        />

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isLoading}>
            {initialData ? "Update" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RestaurantForm;

